import { useMemo, useState } from "react";
import Reveal, { Overline } from "@/components/Reveal";
import { inr } from "@/lib/scroll";

const TENURES = [12, 24, 36, 48, 60, 84];

export default function EmiCalculator() {
  const [amount, setAmount] = useState(1100000);
  const [tenure, setTenure] = useState(60);
  const [rate, setRate] = useState(8.5);

  const { emi, interest, total } = useMemo(() => {
    const r = rate / 1200;
    const e = (amount * r * Math.pow(1 + r, tenure)) / (Math.pow(1 + r, tenure) - 1);
    const t = e * tenure;
    return { emi: Math.round(e), total: Math.round(t), interest: Math.round(t - amount) };
  }, [amount, tenure, rate]);

  return (
    <section id="emi" data-testid="emi-section" className="relative bg-[#05141F] py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#FFFFFF]/8 blur-[160px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Overline>Finance Studio</Overline>
          <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-5xl text-white">
            Know Your EMI<br />Before You <span className="text-outline-red">Drive.</span>
          </h2>
          <p className="mt-6 text-slate-400 leading-relaxed max-w-md">
            Tune the loan amount, tenure and interest rate — our finance desk partners with leading banks
            for instant approvals and same-day sanction letters.
          </p>

          <div className="mt-10 space-y-8">
            <div>
              <div className="flex justify-between items-baseline mb-3">
                <label className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Loan Amount</label>
                <span data-testid="emi-amount-value" className="font-display font-bold text-white text-lg">{inr(amount)}</span>
              </div>
              <input
                data-testid="emi-amount-slider"
                type="range" min="500000" max="13000000" step="50000"
                value={amount} onChange={(e) => setAmount(+e.target.value)}
                className="kia-range w-full"
              />
            </div>

            <div>
              <div className="flex justify-between items-baseline mb-3">
                <label className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Interest Rate</label>
                <span data-testid="emi-rate-value" className="font-display font-bold text-white text-lg">{rate.toFixed(1)}% p.a.</span>
              </div>
              <input
                data-testid="emi-rate-slider"
                type="range" min="7" max="14" step="0.1"
                value={rate} onChange={(e) => setRate(+e.target.value)}
                className="kia-range w-full"
              />
            </div>

            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 block mb-3">Tenure (months)</label>
              <div className="flex flex-wrap gap-2">
                {TENURES.map((t) => (
                  <button
                    key={t}
                    data-testid={`emi-tenure-${t}`}
                    onClick={() => setTenure(t)}
                    className={`font-mono text-xs px-4 py-2.5 border transition-colors duration-300 ${
                      tenure === t
                        ? "bg-[#FFFFFF] border-[#FFFFFF] text-black"
                        : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="emi-result-card" className="relative bg-[#0D1822] border border-white/10 clip-card p-8 sm:p-10">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FFFFFF] to-transparent" />
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">Your Monthly EMI</p>
            <p data-testid="emi-monthly-value" className="mt-3 font-display font-extrabold text-5xl sm:text-6xl text-white tracking-tight">
              {inr(emi)}
            </p>
            <div className="mt-9 grid grid-cols-2 gap-6 border-t border-white/10 pt-7">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Total Interest</p>
                <p data-testid="emi-interest-value" className="mt-2 font-display font-bold text-xl text-[#FFFFFF]">{inr(interest)}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Total Payable</p>
                <p data-testid="emi-total-value" className="mt-2 font-display font-bold text-xl text-white">{inr(total)}</p>
              </div>
            </div>
            <p className="mt-7 text-xs text-slate-600 leading-relaxed">
              *Indicative only. Final EMI depends on lender approval, down payment and on-road pricing.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
